import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { 
  Building2, 
  FileText, 
  Receipt, 
  Briefcase, 
  Phone, 
  Mail,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  Loader2
} from 'lucide-react';

const ClientPortalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPortalData();
  }, [token]);

  const loadPortalData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/portal/${token}`);
      if (!response.ok) {
        throw new Error('Portal not found or link expired');
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status, type) => {
    const styles = {
      // Estimate statuses
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      declined: 'bg-red-100 text-red-700',
      // Invoice statuses
      unpaid: 'bg-amber-100 text-amber-700',
      partial: 'bg-orange-100 text-orange-700',
      paid: 'bg-green-100 text-green-700',
      // Job statuses
      scheduled: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-slate-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Portal Not Found</h1>
          <p className="text-slate-600">
            This portal link may have expired or is invalid. Please contact your service provider for a new link.
          </p>
        </div>
      </div>
    );
  }

  const { client, organization, estimates, invoices, jobs } = data;
  const primaryColor = organization?.brand_primary_color || '#1e3a8a';

  // Calculate totals
  const totalEstimates = estimates?.length || 0;
  const pendingEstimates = estimates?.filter(e => e.status === 'sent').length || 0;
  const totalInvoiced = invoices?.reduce((sum, i) => sum + (i.amount_due || 0), 0) || 0;
  const totalPaid = invoices?.reduce((sum, i) => sum + (i.amount_paid || 0), 0) || 0;
  const outstanding = totalInvoiced - totalPaid;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {organization?.logo_url ? (
                <img 
                  src={organization.logo_url} 
                  alt={organization.company_name} 
                  className="h-10 object-contain"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Building2 className="w-5 h-5" />
                </div>
              )}
              <div>
                <h1 className="font-bold text-lg" style={{ color: primaryColor }}>
                  {organization?.company_name}
                </h1>
                <p className="text-sm text-slate-500">Client Portal</p>
              </div>
            </div>
            <div className="text-right text-sm text-slate-600">
              <p className="font-medium">{client?.name}</p>
              {client?.email && <p>{client.email}</p>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-sm">Estimates</span>
            </div>
            <p className="text-2xl font-bold">{totalEstimates}</p>
            {pendingEstimates > 0 && (
              <p className="text-xs text-amber-600">{pendingEstimates} pending review</p>
            )}
          </div>
          
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Briefcase className="w-4 h-4" />
              <span className="text-sm">Jobs</span>
            </div>
            <p className="text-2xl font-bold">{jobs?.length || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Receipt className="w-4 h-4" />
              <span className="text-sm">Total Invoiced</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(totalInvoiced)}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Outstanding</span>
            </div>
            <p className={`text-2xl font-bold ${outstanding > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {formatCurrency(outstanding)}
            </p>
          </div>
        </div>

        {/* Estimates Section */}
        {estimates?.length > 0 && (
          <section className="mb-6">
            <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: primaryColor }} />
              Estimates
            </h2>
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {estimates.map((estimate, idx) => (
                <div 
                  key={estimate.id}
                  className={`p-4 flex items-center justify-between ${idx > 0 ? 'border-t' : ''}`}
                >
                  <div>
                    <p className="font-medium">{estimate.service_type || 'Estimate'}</p>
                    <p className="text-sm text-slate-500">
                      {estimate.job_address || 'No address'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(estimate.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <div>
                      <p className="font-bold">{formatCurrency(estimate.total || 0)}</p>
                      {getStatusBadge(estimate.status, 'estimate')}
                    </div>
                    {estimate.public_view_token && (
                      <Link to={`/estimate/${estimate.public_view_token}`}>
                        <Button size="sm" variant="outline">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Invoices Section */}
        {invoices?.length > 0 && (
          <section className="mb-6">
            <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Receipt className="w-5 h-5" style={{ color: primaryColor }} />
              Invoices
            </h2>
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {invoices.map((invoice, idx) => (
                <div 
                  key={invoice.id}
                  className={`p-4 flex items-center justify-between ${idx > 0 ? 'border-t' : ''}`}
                >
                  <div>
                    <p className="font-medium">{invoice.invoice_number || `Invoice #${invoice.id.slice(-6)}`}</p>
                    <p className="text-sm text-slate-500">
                      Due: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <div>
                      <p className="font-bold">{formatCurrency(invoice.amount_due || 0)}</p>
                      {invoice.amount_paid > 0 && invoice.amount_paid < invoice.amount_due && (
                        <p className="text-xs text-slate-500">Paid: {formatCurrency(invoice.amount_paid)}</p>
                      )}
                      {getStatusBadge(invoice.status, 'invoice')}
                    </div>
                    {invoice.public_payment_token && invoice.status !== 'paid' && (
                      <Link to={`/invoice/view/${invoice.public_payment_token}`}>
                        <Button size="sm" style={{ backgroundColor: primaryColor }}>
                          Pay Now
                        </Button>
                      </Link>
                    )}
                    {invoice.status === 'paid' && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Jobs Section */}
        {jobs?.length > 0 && (
          <section className="mb-6">
            <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Briefcase className="w-5 h-5" style={{ color: primaryColor }} />
              Jobs
            </h2>
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {jobs.map((job, idx) => (
                <div 
                  key={job.id}
                  className={`p-4 flex items-center justify-between ${idx > 0 ? 'border-t' : ''}`}
                >
                  <div>
                    <p className="font-medium">{job.title || job.job_type || 'Job'}</p>
                    <p className="text-sm text-slate-500">{job.address || 'No address'}</p>
                    {job.scheduled_date && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(job.scheduled_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div>
                    {getStatusBadge(job.status, 'job')}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {!estimates?.length && !invoices?.length && !jobs?.length && (
          <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-lg mb-2">No Activity Yet</h3>
            <p className="text-slate-500">
              You don't have any estimates, invoices, or jobs yet. Contact us to get started!
            </p>
          </div>
        )}

        {/* Contact Footer */}
        <footer className="mt-8 pt-6 border-t text-center">
          <p className="text-sm text-slate-500 mb-2">Questions? Contact us:</p>
          <div className="flex items-center justify-center gap-4">
            {organization?.phone && (
              <a href={`tel:${organization.phone}`} className="flex items-center gap-1 text-sm" style={{ color: primaryColor }}>
                <Phone className="w-4 h-4" />
                {organization.phone}
              </a>
            )}
            {organization?.email && (
              <a href={`mailto:${organization.email}`} className="flex items-center gap-1 text-sm" style={{ color: primaryColor }}>
                <Mail className="w-4 h-4" />
                {organization.email}
              </a>
            )}
          </div>
        </footer>
      </main>
    </div>
  );
};

export default ClientPortalPage;
